import type { AlwaysAiUser } from "../../../shared/api-types";
import type { ShellLook } from "./look-preference";
import {
  hasRole,
  isRdShellPath,
  landingPathForRole,
  shouldRedirectFromAppointments,
  shouldRedirectFromRdWorkspace,
  usesRdShell,
} from "./permissions";
import { hasRailSections } from "./rail-sections";

// Which frame the workspace layout mounts, as one pure decision (NO-754). With
// `always-brand` off the look is always "classic", and the answer is exactly
// what WorkspaceShell answered before the flag existed: the parity table in
// __tests__/workspace-frame.test.ts holds it there.

export type WorkspaceFrame =
  | { kind: "blank" }
  | { kind: "redirect"; to: ReturnType<typeof landingPathForRole> }
  | { kind: "always"; user: AlwaysAiUser }
  | { kind: "rd-shell"; user: AlwaysAiUser }
  | { kind: "legacy" };

export interface WorkspaceFrameInput {
  pathname: string;
  user: AlwaysAiUser | undefined;
  mePending: boolean;
  featuresPending: boolean;
  rdHomeEnabled: boolean;
  /** Already resolved against the flag: "classic" whenever it is off. */
  look: ShellLook;
  /** The look last rendered on this browser, or null. */
  lastLook: ShellLook | null;
}

// Only RD-shell-eligible paths (and RD landings anywhere) depend on the viewer
// and the rd-home flag, so they hold a blank frame instead of flashing the
// wrong chrome; legacy surfaces keep their immediate branded header while
// /api/me loads. A viewer who last saw the rail holds blank everywhere until
// both reads land, so a reload never flashes the classic chrome.
function holdsBlankFrame(input: WorkspaceFrameInput): boolean {
  const viewerPending = input.mePending || input.featuresPending;
  const shellPathPending = isRdShellPath(input.pathname) && viewerPending;
  const isRdViewer = input.user !== undefined && hasRole(input.user, "rd");
  const rdViewerPending = isRdViewer && input.featuresPending;
  const railPending = viewerPending && input.lastLook === "always";

  return shellPathPending || rdViewerPending || railPending;
}

function redirectsToLanding(input: WorkspaceFrameInput, user: AlwaysAiUser): boolean {
  return (
    shouldRedirectFromRdWorkspace(user, input.pathname) ||
    shouldRedirectFromAppointments(user, input.pathname, input.rdHomeEnabled)
  );
}

/**
 * Whether a settled viewer gets the single-rail frame. The rail needs
 * something to show: a VA, or an RD without rd-home, keeps the legacy chrome
 * whatever the look. lib/always-theme.ts reads the same answer, so the theme
 * attribute on <html> never disagrees with the frame (NO-790).
 */
export function rendersAlwaysFrame(
  user: AlwaysAiUser,
  look: ShellLook,
  rdHomeEnabled: boolean
): boolean {
  return look === "always" && hasRailSections(user, rdHomeEnabled);
}

export function chooseWorkspaceFrame(input: WorkspaceFrameInput): WorkspaceFrame {
  if (holdsBlankFrame(input)) return { kind: "blank" };

  const user = input.user;
  if (user === undefined) return { kind: "legacy" };

  if (redirectsToLanding(input, user)) {
    return { kind: "redirect", to: landingPathForRole(user, input.rdHomeEnabled) };
  }

  if (rendersAlwaysFrame(user, input.look, input.rdHomeEnabled)) {
    return { kind: "always", user };
  }

  if (usesRdShell(user, input.pathname, input.rdHomeEnabled)) {
    return { kind: "rd-shell", user };
  }

  return { kind: "legacy" };
}
